import { CountryRegionData } from 'react-country-region-selector'

import {
	AchievementCategory,
	ClubAchievementType,
	ContactInviteStatus,
	LocationUnit,
	LocationUnitLengthType,
	OfferRewardType,
	OfferType,
	PieRole,
	RewardCategory,
	RewardRedemptionType,
	RewardTimeFrameType,
	RewardType,
	UserGender,
	WinnersChosenOption,
} from 'graphql/generated/scheme'
import { ManageClubType } from 'graphql/type'
import { hasClubPermission } from './permissions'

export const GENDERS = [
	{ name: 'Female', value: UserGender.Female },
	{ name: 'Male', value: UserGender.Male },
	{ name: 'Other', value: UserGender.Other },
	{ name: 'Not Disclosed', value: UserGender.NotDisclosed },
]

// OTP resend cooldown in seconds. Must match the backend rate limit
// (Pie.SiteApi OtpConstants.RateLimitSeconds = 30); a shorter value lets the UI
// re-enable Resend before the backend will accept it, producing a 429.
export const OTP_RESEND_COUNTDOWN_SECONDS = 30

export const CLUB_PERMISSIONS = {
	CAN_VIEW_CLUB: 'CanViewClub',
	CAN_ASSIGN_ADMINISTRATORS: 'CanAssignAdministrators',
	CAN_ASSIGN_ACCOUNT_MANAGERS: 'CanAssignAccountManagers',
	CAN_ASSIGN_FINANCE_MANAGERS: 'CanAssignFinanceManagers',
	CAN_ASSIGN_REWARD_MANAGERS: 'CanAssignRewardManagers',
	CAN_TRANSFER_CLUB_OWNERSHIP: 'CanTransferClubOwnership',
	CAN_CREATE_WAYS_TO_EARN_POINTS: 'CanCreateWaysToEarnPoints',
	CAN_CREATE_WAYS_TO_EARN_STATUS: 'CanCreateWaysToEarnStatus',
	CAN_CREATE_WAYS_TO_BUY_POINTS: 'CanCreateWaysToBuyPoints',
	CAN_CREATE_REWARDS: 'CanCreateRewards',
	CAN_MODIFY_EXISTING_REWARDS: 'CanModifyExistingRewards',
	CAN_CREATE_EVENTS: 'CanCreateEvents',
	CAN_PRICE_POINT_BUNDLES: 'CanPricePointBundles',
	CAN_CREATE_TIER: 'CanCreateTier',
	CAN_SEND_POINTS: 'CanSendPoints',
	CAN_SEND_REWARDS: 'CanSendRewards',
	CAN_SEND_ACHIEVEMENTS: 'CanSendAchievements',
	CAN_CONNECT_SOCIAL_ACCOUNTS: 'CanConnectSocialAccounts',
	CAN_REMOVE_SOCIAL_ACCOUNTS: 'CanRemoveSocialAccounts',
	CAN_CHANGE_CLUB_NAME: 'CanChangeClubName',
	CAN_CONNECT_PAYPAL_ACCOUNT: 'CanConnectPaypalAccount',
	CAN_AWARD_REWARDS: 'CanAwardRewards',
	CAN_CHANGE_URL: 'CanChangeUrl',
	CAN_CHANGE_BIO: 'CanChangeBio',
	CAN_SHARE_ACHIEVEMENTS: 'CanShareAchievements',
	CAN_SHARE_REWARDS: 'CanShareRewards',
	CAN_SHARE_CLUB: 'CanShareClub',
	CAN_VIEW_REWARDS: 'CanViewRewards',
	CAN_REVIEW_ACHIEVEMENTS: 'CanReviewAchievements',
	CAN_EDIT_REWARDS: 'CanEditRewards',
	CAN_DELETE_REWARDS: 'CanDeleteRewards',
	CAN_EDIT_ACHIEVEMENT: 'CanEditAchievement',
	CAN_DELETE_ACHIEVEMENTS: 'CanDeleteAchievements',
	CAN_MANAGE_TEAMS: 'CanManageTeams',
	CAN_MANAGE_CLUB: 'CanManageClub',
	CAN_MANAGE_CLUB_ACCOUNTS: 'CanManageClubAccounts',
	CAN_MANAGE_CLUB_ACHIEVEMENTS: 'CanManageClubAchievements',
	CAN_MANAGE_CLUB_DETAIL: 'CanManageClubDetail',
	CAN_MANAGE_CLUB_EVENTS: 'CanManageClubEvents',
	CAN_MANAGE_CLUB_LOYALTY_STATUS: 'CanManageClubLoyaltyStatus',
	CAN_MANAGE_CLUB_OFFERS: 'CanManageClubOffers',
	CAN_MANAGE_CLUB_REP_POINT: 'CanManageClubRepPoint',
	CAN_TRADE_CLUB_POINTS: 'CanTradeClubPoints',
	CAN_SEND_MEMBER_REQUESTS: 'CanSendMemberRequests',
	CAN_APPROVE_MEMBER_REQUESTS: 'CanApproveMemberRequests',
	CAN_SEND_COLLABORATOR_REQUESTS: 'CanSendCollaboratorRequests',
	CAN_APPROVE_COLLABORATOR_REQUESTS: 'CanApproveCollaboratorRequests',
	CAN_EDIT_CLUB: 'CanEditClub',
	CAN_EDIT_TIERS: 'CanEditTiers',
	CAN_REMOVE_CLUB_TIER: 'CanRemoveClubTier',
	CAN_CHANGE_SLUG: 'CanChangeSlug',
	CAN_CREATE_OFFER: 'CanCreateOffer',
	CAN_EDIT_OFFER: 'CanEditOffer',
	CAN_DELETE_OFFER: 'CanDeleteOffer',
	CAN_CREATE_ACHIEVEMENT: 'CanCreateAchievement',
	CAN_ISSUE_POINTS: 'CanIssuePoints',
	CAN_TAKE_POINTS: 'CanTakePoints',
	CAN_CREATE_POINTS_CLASS: 'CanCreatePointsClass',
	CAN_DELETE_POINTS_CLASS: 'CanDeletePointsClass',
} as const

/**
 * Returns true when the app is running in dev/QA with the Dev Mode toggle enabled.
 * Pass an optional feature name to tag this call site — the Vite dev-features plugin
 * scans the source for these names at build time and lists them in the Dev Mode tooltip.
 */
export const devMode = (_featureName: string) => {
	return import.meta.env.VITE_ENV !== 'prod' && import.meta.env.VITE_ENV !== 'stage' && getGlobalOverride()
}

const getGlobalOverride = (): boolean | null => {
	try {
		const parsed = JSON.parse(localStorage.getItem('featureFlags:globalOverride') ?? 'null')
		return typeof parsed === 'boolean' ? parsed : null
	} catch {
		return null
	}
}

export const getNavigationItems = () => {
	const isDevModeEnabled = Boolean(devMode('Offers explore page & nav item'))
	return [
		{
			name: 'Home',
			href: '/home',
			icon: 'home',
			disabled: false,
			hidden: !isDevModeEnabled,
		},
		{ name: 'Events', href: '/events', icon: 'event', disabled: false, hidden: false },
		{
			name: 'Offers',
			href: '/offers/explore',
			icon: 'rewards',
			disabled: false,
			hidden: !isDevModeEnabled,
		},
		{ name: 'Profile', href: '/profile', icon: 'user', disabled: false, hidden: false },
	].filter(item => !item.hidden)
}

export const getManageEventsNavigationItems = () => [
	{ name: 'Contacts', href: '/manage/rep-clubs/contacts', icon: 'people', disabled: false },
	{ name: 'Revenue', href: '/manage/rep-clubs/events/revenue', icon: 'dollar', disabled: false },
	{ name: 'Inbox', href: '/manage/rep-clubs/inbox', icon: 'inbox', disabled: false },
]

export const HOME_TAB_ITEMS = [
	{ name: 'Featured', value: 'featured' },
	{ name: 'Newcomers', value: 'newcomers' },
	{ name: 'Watchlist', value: 'watchlist' },
]

export const TRADE_TAB_ITEMS = [
	{ name: 'Buy', value: 'buy' },
	{ name: 'Send', value: 'send' },
	{ name: 'Send to my pie', value: 'sendToPie' },
]

export const DEFAULT_INFORMATION = {
	avatarImageUrl: '/images/default/avatar.svg',
	name: 'Unknown',
	rank: 0,
	iconPie: '/images/common/pie-card/image-pie-1.jpg',
	iconAssets: [
		'/images/common/pie-image/icon-1.svg',
		'/images/common/pie-image/icon-2.svg',
		'/images/common/pie-image/icon-3.svg',
		'/images/common/pie-image/icon-4.svg',
		'/images/common/pie-image/icon-5.svg',
		'/images/common/pie-image/icon-6.svg',
		'/images/common/pie-image/icon-7.svg',
	],
	rewardImageUrl: '/images/common/reward-1.jpg',
	pieImageUrl: '/images/default/pie.png',
}

export const REWARDS_TAB_ITEMS = [
	{ name: 'All', value: 'all' },
	{ name: 'Not participating', value: 'not-participating' },
	{ name: 'Participating', value: 'participating' },
	{ name: 'Awarded', value: 'awarded' },
	{ name: 'Expired', value: 'expired' },
]

export const EVENTS_TAB_ITEMS = [
	{ name: 'Explore', value: 'explore' },
	{ name: 'My Events', value: 'my-events' },
	{ name: 'Near Me', value: 'near-me' },
	{ name: 'This Week', value: 'this-week' },
]

export const STRIPE_TAB_ITEMS = [
	{ name: 'Account', value: 'account' },
	{ name: 'Payments', value: 'payments' },
	{ name: 'Payouts', value: 'payouts' },
	{ name: 'Documents', value: 'documents' },
]

export const OFFERS_TAB_ITEMS = [
	{ name: 'Explore', value: 'explore', disabled: false },
	{ name: 'My Offers', value: 'my-offer', disabled: false },
	{ name: 'Featured', value: 'featured', disabled: false },
	{ name: 'This Week', value: 'this-week', disabled: false },
]

export const REP_CLUB_DETAIL_TAB_ITEMS = [
	{ label: 'Events', value: 'events', name: 'Events' },
	{ label: 'Offers', value: 'offers', name: 'Offers' },
]

export const TRADE_HUB_TAB_ITEMS = [
	{ name: 'Your Rep Points', value: 'your-rep-points' },
	{ name: 'Trade Requests', value: 'trade-requests' },
	{ name: 'Trade History', value: 'trade-history' },
]

export const ASSET_CATEGORY_ITEMS = [
	{ name: 'Background', value: 'background', icon: 'background' },
	{ name: 'Napkin', value: 'napkin', icon: 'napkin' },
	{ name: 'Plate', value: 'plate', icon: 'plate' },
	{ name: 'Crust', value: 'crust', icon: 'crust' },
	{ name: 'Filling', value: 'filling', icon: 'filling' },
	{ name: 'Topping', value: 'topping', icon: 'topping' },
	{ name: 'Utensil', value: 'fork', icon: 'fork' },
]

export const PIE_HUB_TAB_ITEMS = [
	{ name: 'Details', value: 'details' },
	{ name: 'Appearance', value: 'appearance' },
	{ name: 'Slices', value: 'slices' },
	{ name: 'Summary', value: 'summary' },
]

export const PIE_HUB_OVERVIEW_TAB_ITEMS = [
	{ name: 'Overview', value: 'overview' },
	{ name: 'Sales', value: 'sales' },
	{ name: 'Rewards', value: 'rewards' },
	{ name: 'Achievements', value: 'achievements' },
]

export const getRepClubTabItems = (club: ManageClubType) => {
	return [
		{
			name: 'Details',
			value: 'edit',
			disabled: false,
			hidden:
				!devMode('Manage Rep Club tabs') || !hasClubPermission(club, CLUB_PERMISSIONS.CAN_MANAGE_CLUB_DETAIL),
		},
		{
			name: 'Rep Points',
			value: 'rep-points',
			disabled: false,
			hidden:
				!devMode('Manage Rep Club tabs') ||
				!hasClubPermission(club, CLUB_PERMISSIONS.CAN_MANAGE_CLUB_REP_POINT),
		},
		{
			name: 'Loyalty Tiers',
			value: 'loyalty-tiers',
			disabled: false,
			hidden:
				!devMode('Manage Rep Club tabs') ||
				!hasClubPermission(club, CLUB_PERMISSIONS.CAN_MANAGE_CLUB_LOYALTY_STATUS),
		},
		{
			name: 'Events',
			value: 'events',
			disabled: false,
			hidden: club && !hasClubPermission(club, CLUB_PERMISSIONS.CAN_MANAGE_CLUB_EVENTS),
		},
		{
			name: 'Offers',
			value: 'offers',
			disabled: false,
			hidden:
				!devMode('Manage Rep Club tabs') || !hasClubPermission(club, CLUB_PERMISSIONS.CAN_MANAGE_CLUB_OFFERS),
		},
		{
			name: 'Achievements',
			value: 'achievements',
			disabled: false,
			hidden:
				!devMode('Manage Rep Club tabs') ||
				!hasClubPermission(club, CLUB_PERMISSIONS.CAN_MANAGE_CLUB_ACHIEVEMENTS),
		},
		{
			name: 'Accounts',
			value: 'accounts',
			disabled: false,
			hidden:
				!devMode('Manage Rep Club tabs') || !hasClubPermission(club, CLUB_PERMISSIONS.CAN_MANAGE_CLUB_ACCOUNTS),
		},
		{
			name: 'Team',
			value: 'team',
			disabled: false,
			hidden: !devMode('Manage Rep Club tabs') || !hasClubPermission(club, CLUB_PERMISSIONS.CAN_MANAGE_TEAMS),
		},
	].filter(item => !item.hidden)
}

export const REWARD_CREATE_TAB_ITEMS = [
	{ name: 'Details', value: 'details' },
	{ name: 'Requirements', value: 'requirements' },
	{ name: 'Time Frame', value: 'time-frame' },
	{ name: 'Configurations', value: 'configurations' },
	{ name: 'Redemption', value: 'redemption' },
	{ name: 'Achievement', value: 'achievement' },
]

export const REQUIREMENTS = [
	{ name: '# of slices from your Pie', value: 'own-slices', limit: 1 },
	{ name: '# of slices from another Pie', value: 'own-slices-other', limit: 5 },
	{ name: 'Achievements', value: 'achievement-badge', limit: 1 },
]

export const TIME_FRAME = [
	{ name: 'After end date', value: WinnersChosenOption.AfterEndDate },
	{ name: 'After start date', value: WinnersChosenOption.AfterStartDate },
]

export const REWARD_TYPES = [
	{ name: 'Digital', value: RewardType.Digital },
	{ name: 'Physical', value: RewardType.Physical },
]

export const REWARD_CATEGORIES = [
	{ name: 'Invite', value: RewardCategory.Invite },
	{ name: 'Event', value: RewardCategory.AccessPass },
	{ name: 'Vip Pass', value: RewardCategory.VipPass },
	{ name: 'Race', value: RewardCategory.Race },
	{ name: 'Meet up', value: RewardCategory.MeetUp },
	{ name: 'Coupon', value: RewardCategory.Coupon },
	{ name: 'Merchandise/ Give-a-way', value: RewardCategory.MerchandiseGiveAWay },
	{ name: 'Raffle', value: RewardCategory.Raffle },
	{ name: 'Community', value: RewardCategory.Community },
	{ name: 'Exclusive Content', value: RewardCategory.ExclusiveContent },
	{ name: 'Other', value: RewardCategory.Other },
]

export const ACHIVEMENT_CATEGORIES = [
	{ name: 'Launch Day supporter', value: AchievementCategory.LaunchDaySupporter },
	{ name: 'OG Supporter', value: AchievementCategory.OgSupporter },
	{ name: 'Participation Badge', value: AchievementCategory.ParticipationBadge },
	{ name: 'Event badge', value: AchievementCategory.EventBadge },
	{ name: 'Brand badge', value: AchievementCategory.BrandBadge },
	{ name: 'Product Badge', value: AchievementCategory.ProductBadge },
	{ name: 'Community Badge', value: AchievementCategory.CommunityBadge },
	{ name: 'Tier Badge Emerald', value: AchievementCategory.TierBadgeEmerald },
	{ name: 'Tier Badge Ruby', value: AchievementCategory.TierBadgeRuby },
	{ name: 'Tier Badge Diamond', value: AchievementCategory.TierBadgeDiamond },
	{ name: 'Other', value: AchievementCategory.Other },
]

export const ACHIEVEMENT_TYPES = [
	{
		name: 'Launch Day supporter',
		value: ClubAchievementType.LaunchDaySupporter,
		icon: 'increase',
		description: `Welcome fans into your exclusive community! Elevate relationships with your audience, collaborate with brands, and gain insights into fan interactions.`,
	},
	{
		name: 'Participation Badge',
		value: ClubAchievementType.ParticipationBadge,
		icon: 'star-in-star',
		description: `Welcome fans into your exclusive community! Elevate relationships with your audience, collaborate with brands, and gain insights into fan interactions.`,
	},
	{
		name: 'Event Badge',
		value: ClubAchievementType.EventBadge,
		icon: 'calendar',
		description: `Welcome fans into your exclusive community! Elevate relationships with your audience, collaborate with brands, and gain insights into fan interactions.`,
	},
	{
		name: 'Brand Badge',
		value: ClubAchievementType.BrandBadge,
		icon: 'rep-points',
		description: `Welcome fans into your exclusive community! Elevate relationships with your audience, collaborate with brands, and gain insights into fan interactions.`,
	},
	{
		name: 'Product Badge',
		value: ClubAchievementType.ProductBadge,
		icon: 'shirt',
		description: `Welcome fans into your exclusive community! Elevate relationships with your audience, collaborate with brands, and gain insights into fan interactions.`,
	},
	{
		name: 'Community Badge',
		value: ClubAchievementType.CommunityBadge,
		icon: 'community-user',
		description: `Welcome fans into your exclusive community! Elevate relationships with your audience, collaborate with brands, and gain insights into fan interactions.`,
	},
	{
		name: 'Tier Badge',
		value: ClubAchievementType.TierBadge,
		icon: 'cup',
		description: 'Lorem ipsum dolor sit amet consectetur. Fermentum non in vitae egestas tellus arc.',
	},
	{
		name: 'None',
		value: 'none',
		icon: 'banned',
		description: 'Lorem ipsum dolor sit amet consectetur. Fermentum non in vitae egestas tellus arc.',
	},
]

export const REDEEM_TYPES = [
	{ name: 'Promo Code', value: RewardRedemptionType.PromoCode },
	{ name: 'Access Pass', value: RewardRedemptionType.AccessPass },
	{ name: 'Invite Link', value: RewardRedemptionType.InviteLink },
	{ name: 'Location Information', value: RewardRedemptionType.LocationInformation },
	{ name: 'User Contact Information', value: RewardRedemptionType.CustomerInformation },
]

export const PIE_ROLES = [
	{ name: 'Admin', value: PieRole.Admin },
	{ name: 'Account Manager', value: PieRole.AccountManager },
	{ name: 'Reward Manager', value: PieRole.RewardManager },
]

export const PIE_PERMISSIONS = {
	CAN_ASSIGN_ADMINISTRATORS: 'CAN_ASSIGN_ADMINISTRATORS',
	CAN_ASSIGN_ACCOUNT_MANAGERS: 'CAN_ASSIGN_ACCOUNT_MANAGERS',
	CAN_ASSIGN_REWARD_MANAGERS: 'CAN_ASSIGN_REWARD_MANAGERS',
	CAN_TRANSFER_OWNERSHIP: 'CAN_TRANSFER_OWNERSHIP',
	CAN_CREATE_REWARDS: 'CAN_CREATE_REWARDS',
	CAN_MODIFY_EXISTING_REWARDS: 'CAN_MODIFY_EXISTING_REWARDS',
	CAN_SET_PRICE_PER_SLICE: 'CAN_SET_PRICE_PER_SLICE',
	CAN_UPGRADE_PIE: 'CAN_UPGRADE_PIE',
	CAN_SEND_SLICES: 'CAN_SEND_SLICES',
	CAN_SEND_REWARDS: 'CAN_SEND_REWARDS',
	CAN_SEND_ACHIEVEMENTS: 'CAN_SEND_ACHIEVEMENTS',
	CAN_CONNECT_SOCIAL_ACCOUNTS: 'CAN_CONNECT_SOCIAL_ACCOUNTS',
	CAN_REMOVE_SOCIAL_ACCOUNTS: 'CAN_REMOVE_SOCIAL_ACCOUNTS',
	CAN_CHANGE_PIE_NAME: 'CAN_CHANGE_PIE_NAME',
	CAN_CHANGE_PIE_APPEARANCE: 'CAN_CHANGE_PIE_APPEARANCE',
	CAN_RELEASE_MORE_SLICES_FOR_SALE: 'CAN_RELEASE_MORE_SLICES_FOR_SALE',
	CAN_CONNECT_PAYPAL_ACCOUNT: 'CAN_CONNECT_PAYPAL_ACCOUNT',
	CAN_AWARD_REWARDS: 'CAN_AWARD_REWARDS',
	CAN_CHANGE_PIE_URL: 'CAN_CHANGE_PIE_URL',
	CAN_CHANGE_PIE_BIO: 'CAN_CHANGE_PIE_BIO',
	CAN_SHARE_ACHIEVEMENTS: 'CAN_SHARE_ACHIEVEMENTS',
	CAN_SHARE_REWARDS: 'CAN_SHARE_REWARDS',
	CAN_SHARE_PIE: 'CAN_SHARE_PIE',
	CAN_VIEW_REWARDS: 'CAN_VIEW_REWARDS',
	CAN_VIEW_ACHIEVEMENTS: 'CAN_VIEW_ACHIEVEMENTS',
}

export const MEDIA_FOLDER = {
	rewardAvatars: 'reward-avatars',
	achievementAvatars: 'achievement-avatars',
}

export const EXCLUDE_COUNTRIES = ['kp', 'cu', 'ir', 'sy', 've', 'ru']

export const COUNTRY_DATA = CountryRegionData.filter(
	(item: any) => !EXCLUDE_COUNTRIES.includes(item[1].toLowerCase())
).map(item => ({ name: item[0], value: item[1] }))

export const SEARCH_TAB_ITEMS = [
	{ name: 'All', value: 'all', disabled: false },
	{ name: 'People', value: 'people', disabled: true },
	{ name: 'Pies', value: 'pies', disabled: true },
]

export const getNotificationTabItems = () => {
	return [
		{ name: 'All', value: 'all', hidden: false },
		{ name: 'Requests', value: 'requests', hidden: false },
		{ name: 'Offers', value: 'offers', hidden: !devMode('Notification tabs: Offers & Achievements') },
		{ name: 'Achievements', value: 'achievements', hidden: !devMode('Notification tabs: Offers & Achievements') },
		{ name: 'Events', value: 'events', hidden: false },
	].filter(item => !item.hidden)
}

export const PIE_HUB_EDIT_TAB_ITEMS = [
	{ name: 'Details', value: 'details' },
	{ name: 'Appearance', value: 'appearance' },
	{ name: 'Slices', value: 'slices' },
	{ name: 'Upgrade', value: 'upgrade' },
	{ name: 'Manage', value: 'manage' },
]

export const NUMBER_SLICES_OPTIONS = [
	{
		slice: 10000,
		price: 0,
	},
	{
		slice: 100000,
		price: 10,
	},
	{
		slice: 1000000,
		price: 100,
	},
	{
		slice: 10000000,
		price: 1000,
	},
	{
		slice: 50000000,
		price: 5000,
	},
	{
		slice: 100000000,
		price: 10000,
	},
	{
		slice: 500000000,
		price: 50000,
	},
	{
		slice: 1000000000,
		price: 100000,
	},
]

export const MANDATORY_APPEARENCE_CATEGORIES = ['background', 'napkin', 'crust', 'filling']

export const PERCENTAGE_SPLIT_VALUE = 0.2

export enum SocialScheme {
	Instagram = 'instagram',
	Facebook = 'facebook',
	Twitter = 'twitter',
	Youtube = 'youtube',
	TikTok = 'tikTok',
	Spotify = 'spotify',
	Twitch = 'twitch',
	Apple = 'apple',
	LinkedIn = 'linkedin',
	AmazonMusic = 'amazonmusic',
}

export const SOCIAL_CONNECTIONS = [
	{
		scheme: SocialScheme.Instagram,
		displayName: 'Instagram',
		icon: 'instagram',
		url: 'https://instagram.com',
		hidden: import.meta.env.VITE_ENABLE_FEATURE_CONNECT_INSTAGRAM === 'true' ? false : true,
	},
	{
		scheme: SocialScheme.Facebook,
		displayName: 'Facebook',
		icon: 'facebook',
		url: 'https://facebook.com',
		hidden: import.meta.env.VITE_ENABLE_FEATURE_CONNECT_FACEBOOK === 'true' ? false : true,
	},
	{
		scheme: SocialScheme.Twitter,
		secondaryScheme: 'x',
		displayName: 'X',
		icon: 'twitter',
		url: 'https://x.com',
		hidden: import.meta.env.VITE_ENABLE_FEATURE_CONNECT_X === 'true' ? false : true,
	},
	{
		scheme: SocialScheme.Youtube,
		displayName: 'Youtube',
		icon: 'youtube',
		url: 'https://youtube.com',
		hidden: import.meta.env.VITE_ENABLE_FEATURE_CONNECT_YOUTUBE === 'true' ? false : true,
	},
	{
		scheme: SocialScheme.TikTok,
		displayName: 'Tiktok',
		icon: 'tiktok',
		url: 'https://tiktok.com',
		hidden: import.meta.env.VITE_ENABLE_FEATURE_CONNECT_TIKTOK === 'true' ? false : true,
	},
	{
		scheme: SocialScheme.Spotify,
		displayName: 'Spotify',
		icon: 'spotify',
		url: 'https://spotify.com',
		hidden: import.meta.env.VITE_ENABLE_FEATURE_CONNECT_SPOTIFY === 'true' ? false : true,
	},
	{
		scheme: SocialScheme.Twitch,
		displayName: 'Twitch',
		icon: 'twitch',
		url: 'https://twitch.tv',
		hidden: import.meta.env.VITE_ENABLE_FEATURE_CONNECT_TWITCH === 'true' ? false : true,
	},
	{
		scheme: SocialScheme.Apple,
		displayName: 'Apple',
		icon: 'apple',
		url: 'https://apple.com',
		hidden: import.meta.env.VITE_ENABLE_FEATURE_CONNECT_APPLE === 'true' ? false : true,
	},
	{
		scheme: SocialScheme.LinkedIn,
		displayName: 'LinkedIn',
		icon: 'linkedin',
		url: 'https://linkedin.com',
		hidden: import.meta.env.VITE_ENABLE_FEATURE_CONNECT_LINKEDIN === 'true' ? false : true,
	},
	// {
	// 	scheme: 'amazonmusic',
	// 	displayName: 'Amazon Music',
	// 	icon: 'amazonmusic',
	// 	url: 'https://music.amazon.com',
	// 	hidden: false,
	// },
].filter(item => !item.hidden)

export const getSocialConnections = () => {
	const allowedSchemes = devMode('Social connections (all platforms)')
		? null // Show all social connections in dev mode
		: [SocialScheme.Twitter, SocialScheme.Youtube, SocialScheme.TikTok, SocialScheme.Twitch, SocialScheme.Instagram]

	return allowedSchemes ? SOCIAL_CONNECTIONS.filter(item => allowedSchemes.includes(item.scheme)) : SOCIAL_CONNECTIONS
}

export const getEventCreateTabItems = () => {
	return [
		{ name: 'Info', value: 'info', hidden: false },
		{ name: 'Roles', value: 'staff', hidden: false },
		{ name: 'Tickets', value: 'guest', hidden: false },
		{ name: 'Settings', value: 'settings', hidden: false },
		{ name: 'Questions', value: 'questions', hidden: false },
		{
			name: 'Clubs',
			value: 'rep-clubs',
			hidden: false,
		},
		{ name: 'Preview', value: 'preview', hidden: false },
	].filter(item => !item.hidden)
}

export const MINIMUM_PRICE_PER_SLICE_VALUE = 3

export const DEFAULT_EXTENDED_MINUTES = 15

export const DOCUMENT_UPLOAD_LIMIT_SIZE = 10

export const IMAGE_UPLOAD_LIMIT_SIZE = 30

export const MEASURING_LENGTH_UNITS = [
	{ name: 'mi', value: LocationUnitLengthType.Miles },
	{ name: 'km', value: LocationUnitLengthType.Kilometers },
]

export const LOCATION_UNITS = [
	{ name: 'mi', value: LocationUnit.Miles },
	{ name: 'km', value: LocationUnit.Kilometers },
]

export const DEFAULT_MAP_COORDS = {
	lat: 34.052235,
	lng: -118.243683,
}

export const DEFAULT_MAP_RADIUS = 0.5

export const DEFAULT_MAX_SLICES = 10000000

export const EMPTY_QUESTION = {
	question: '',
	isRequired: false,
	showPublicTickets: true,
	showPrivateTickets: true,
}

export const EMPTY_REP_POINT_BUCKET = {
	pointsAmount: '',
	repPointsMultiplier: '',
	originalPrice: '',
}

export const REP_CLUBS_CREATE_TAB_ITEMS = [
	{ name: 'Club Type', value: 'club-type', formTitle: 'Select Your Club Type', hidden: true },
	{ name: 'Details', value: 'details', formTitle: 'Details', hidden: false },
	{ name: 'Image', value: 'image', formTitle: 'Club Image', hidden: false },
	{ name: 'Accounts', value: 'accounts', formTitle: 'Connect Accounts', hidden: false },
	{ name: 'Preview', value: 'preview', formTitle: 'Review Club', hidden: false },
].filter(item => !item.hidden)
export const DEFAULT_REP_SCORE = 10

export const TICKET_TYPES = [
	{
		name: 'Add Free Ticket',
		value: 'free',
		icon: 'ticket',
		description: `Offer complimentary access for guests to access your event.`,
	},
	{
		name: 'Add Paid Ticket',
		value: 'paid',
		icon: 'dollar',
		description: `Charge people to attend your event and set prices for ticket types or exclusive areas.`,
	},
]

export const OFFER_CREATE_TAB_ITEMS = [
	{ name: 'Details', value: 'details', hidden: false },
	{ name: 'Requirements', value: 'requirements', hidden: false },
	{ name: 'Time Frame', value: 'time-frame', hidden: false },
	{ name: 'Settings', value: 'settings', hidden: false },
	{ name: 'Redemption', value: 'redemption', hidden: false },
	{ name: 'Achievement', value: 'achievement', hidden: false },
	{ name: 'Preview', value: 'preview', hidden: false },
].filter(item => !item.hidden)

export const OFFER_TYPES = [
	{
		name: 'Ways To Earn Points',
		value: OfferType.EarnPoints,
		icon: 'rep-points',
		description: `Welcome fans into your exclusive community! Elevate relationships with your audience, collaborate with brands, and gain insights into fan interactions.`,
	},
	{
		name: 'Ways To Buy Points',
		value: OfferType.BuyPoints,
		icon: 'dollar',
		description: `Welcome fans into your exclusive community! Elevate relationships with your audience, collaborate with brands, and gain insights into fan interactions.`,
	},
	{
		name: 'Ways To Earn Status',
		value: OfferType.EarnStatus,
		icon: 'star-in-star',
		description: `Welcome fans into your exclusive community! Elevate relationships with your audience, collaborate with brands, and gain insights into fan interactions.`,
	},
	{
		name: 'Ways To Reward Members',
		value: OfferType.RewardMembers,
		icon: 'rewards',
		description: `Welcome fans into your exclusive community! Elevate relationships with your audience, collaborate with brands, and gain insights into fan interactions.`,
	},
]

export const REWARD_EVENT_TYPES = [
	{
		name: 'Invite',
		value: OfferRewardType.Invite,
		icon: 'email',
		description: `Welcome fans into your exclusive community! Elevate relationships with your audience, collaborate with brands, and gain insights into fan interactions.`,
	},
	{
		name: 'Event',
		value: OfferRewardType.Event,
		icon: 'calendar',
		description: `Welcome fans into your exclusive community! Elevate relationships with your audience, collaborate with brands, and gain insights into fan interactions.`,
	},
	{
		name: 'VIP Pass',
		value: OfferRewardType.VipPass,
		icon: 'star-in-star',
		description: `Welcome fans into your exclusive community! Elevate relationships with your audience, collaborate with brands, and gain insights into fan interactions.`,
	},
	{
		name: 'Meet Up',
		value: OfferRewardType.MeetUp,
		icon: 'shake-hand',
		description: `Welcome fans into your exclusive community! Elevate relationships with your audience, collaborate with brands, and gain insights into fan interactions.`,
	},
	{
		name: 'Buy Now',
		value: OfferRewardType.BuyNow,
		icon: 'dollar',
		description: `Welcome fans into your exclusive community! Elevate relationships with your audience, collaborate with brands, and gain insights into fan interactions.`,
	},
	{
		name: 'Coupon',
		value: OfferRewardType.Coupon,
		icon: 'price',
		description: `Welcome fans into your exclusive community! Elevate relationships with your audience, collaborate with brands, and gain insights into fan interactions.`,
	},
	{
		name: 'Merch Giveaway',
		value: OfferRewardType.MerchGiveaway,
		icon: 'shirt',
		description: `Welcome fans into your exclusive community! Elevate relationships with your audience, collaborate with brands, and gain insights into fan interactions.`,
	},
	{
		name: 'Community',
		value: OfferRewardType.Community,
		icon: 'community-user',
		description: `Welcome fans into your exclusive community! Elevate relationships with your audience, collaborate with brands, and gain insights into fan interactions.`,
	},
	{
		name: 'Exclusive Content',
		value: OfferRewardType.ExclusiveContent,
		icon: 'exclusive',
		description: `Welcome fans into your exclusive community! Elevate relationships with your audience, collaborate with brands, and gain insights into fan interactions.`,
	},
	{
		name: 'Other',
		value: OfferRewardType.Other,
		icon: 'other',
		description: `Welcome fans into your exclusive community! Elevate relationships with your audience, collaborate with brands, and gain insights into fan interactions.`,
	},
]

export const REP_SCORE_FILTER_OPTIONS = [
	{ name: 'All', value: null },
	{ name: '0 - 50', value: '0-50' },
	{ name: '50-80', value: '50-80' },
	{ name: '80', value: '80-' },
]

export const GENDER_FILTER_OPTIONS = [
	{ name: 'All', value: null },
	{ name: 'Male', value: UserGender.Male },
	{ name: 'Female', value: UserGender.Female },
	{ name: 'Other', value: UserGender.Other },
]

export const INVITE_STATUS_FILTER_OPTIONS = [
	{ name: 'All', value: ContactInviteStatus.All },
	{ name: 'Invite Pending', value: ContactInviteStatus.InvitePending },
	{ name: 'Invited', value: ContactInviteStatus.Invited },
	{ name: 'Not Invited', value: ContactInviteStatus.NotInvited },
]

export const AGE_RANGE_FILTER_OPTIONS = [
	{ name: 'All', value: null },
	{ name: '18-24', value: '18-24' },
	{ name: '25-34', value: '25-34' },
	{ name: '35-44', value: '35-44' },
	{ name: '45+', value: '45-' },
]

export const COUNTRY_DATA_FILTER_OPTIONS = [{ name: 'All', value: null }, ...COUNTRY_DATA]

export const REWARD_TIME_FRAME_METHOD = [
	{
		name: 'Promotion',
		value: RewardTimeFrameType.Promotion,
		icon: 'hand-speaker',
		description: `Lorem ipsum dolor sit amet consectetur. Fermentum non in vitae egestas tellus arc.`,
	},
	{
		name: 'Draw',
		value: RewardTimeFrameType.Draw,
		icon: 'ticket',
		description: `Lorem ipsum dolor sit amet consectetur. Fermentum non in vitae egestas tellus arc.`,
	},
]

export const REWARD_REQUIREMENTS = [
	{ name: 'Price', value: 'price', limit: 1 },
	{ name: 'Loyalty Status', value: 'loyalty-status', limit: 5 },
	{ name: 'Rep Points', value: 'rep-points', limit: 5 },
	{ name: 'Rep Score', value: 'rep-score', limit: 5 },
	{ name: 'Achievements', value: 'achievement', limit: 1 },
	{ name: 'None', value: 'none', limit: 1 },
]

export const DEFAULT_TICKET_COLOR = '#5D5F70'

// Comprehensive Authenticator AAGUID to device mapping based on Corbado's database
// Reference: https://www.corbado.com/glossary/aaguid
export const AUTHENTICATOR_INFO: { [key: string]: { name: string; icon: string; type: string } } = {
	// Generic/Unknown (special case)
	'00000000-0000-0000-0000-000000000000': { name: 'Generic Authenticator', icon: 'shield-alt', type: 'Unknown' },

	// Google Password Manager
	'ea9b8d66-4d01-1d21-3ce4-b6b48cb575d4': { name: 'Google Password Manager', icon: 'key', type: 'Platform' },

	// Chrome/Chromium
	'adce0002-35bc-c60a-648b-0b25f1f05503': { name: 'Chrome on Mac', icon: 'chrome', type: 'Platform' },
	'b5397666-4885-aa6b-cebf-e52262a439a2': { name: 'Chromium Browser', icon: 'chrome', type: 'Platform' },
	'771b48fd-d3d4-4f74-9232-fc157ab0507a': { name: 'Edge on Mac', icon: 'edge', type: 'Platform' },

	// Windows Hello
	'08987058-cadc-4b81-b6e1-30de50dcbe96': { name: 'Windows Hello', icon: 'fingerprint', type: 'Platform' },
	'9ddd1817-af5a-4672-a2b9-3e3dd95000a9': { name: 'Windows Hello', icon: 'fingerprint', type: 'Platform' },
	'6028b017-b1d4-4c02-b4b3-afcdafc96bb2': { name: 'Windows Hello', icon: 'fingerprint', type: 'Platform' },

	// iCloud Keychain
	'dd4ec289-e01d-41c9-bb89-70fa845d4bf2': { name: 'iCloud Keychain (Managed)', icon: 'apple', type: 'Platform' },
	'fbfc3007-154e-4ecc-8c0b-6e020557d7bd': { name: 'iCloud Keychain', icon: 'apple', type: 'Platform' },

	// Password Managers
	'531126d6-e717-415c-9320-3d9aa6981239': { name: 'Dashlane', icon: 'key', type: 'Password Manager' },
	'bada5566-a7aa-401f-bd96-45619a55120d': { name: '1Password', icon: 'key', type: 'Password Manager' },
	'b84e4048-15dc-4dd0-8640-f4f60813c8af': { name: 'NordPass', icon: 'key', type: 'Password Manager' },
	'0ea242b4-43c4-4a1b-8b17-dd6d0b6baec6': { name: 'Keeper', icon: 'key', type: 'Password Manager' },
	'f3809540-7f14-49c1-a8b3-8f813b225541': { name: 'Enpass', icon: 'key', type: 'Password Manager' },
	'd548826e-79b4-db40-a3d8-11116f7e8349': { name: 'Bitwarden', icon: 'key', type: 'Password Manager' },
	'50726f74-6f6e-5061-7373-50726f746f6e': { name: 'Proton Pass', icon: 'key', type: 'Password Manager' },
	'fdb141b2-5d84-443e-8a35-4698c205a502': { name: 'KeePassXC', icon: 'key', type: 'Password Manager' },

	// Mobile Platform
	'53414d53-554e-4700-0000-000000000000': { name: 'Samsung Pass', icon: 'fingerprint', type: 'Platform' },

	// Thales SDK
	'66a0ccb3-bd6a-191f-ee06-e375c50b9846': { name: 'Thales Bio iOS SDK', icon: 'fingerprint', type: 'SDK' },
	'8836336a-f590-0921-301d-46427531eee6': { name: 'Thales Bio Android SDK', icon: 'fingerprint', type: 'SDK' },
	'cd69adb5-3c7a-deb9-3177-6800ea6cb72a': { name: 'Thales PIN Android SDK', icon: 'lock', type: 'SDK' },
	'17290f1e-c212-34d0-1423-365d729f09d9': { name: 'Thales PIN iOS SDK', icon: 'lock', type: 'SDK' },

	// Other/Specialized
	'39a5647e-1853-446c-a1f6-a79bae9f5bc7': { name: 'IDmelon', icon: 'id-card', type: 'Security Key' },
	'01020304-0506-0708-0102-030405060708': { name: 'Virtual Authenticator', icon: 'cog', type: 'Virtual' },

	// YubiKey devices (legacy)
	'cb69481e-8ff7-4039-93ec-0a2729a154a8': { name: 'YubiKey 5 Series', icon: 'key', type: 'Security Key' },
	'f8a011f3-8c0a-4d15-8006-17111f9edc7d': { name: 'YubiKey 5 FIPS', icon: 'key', type: 'Security Key' },
	'2fc0579f-8113-47ea-b116-bb5a8db9202a': { name: 'YubiKey 5Ci', icon: 'key', type: 'Security Key' },
	'73bb0cd4-e502-49b8-9c6f-b59445bf720b': { name: 'YubiKey 5 Nano', icon: 'key', type: 'Security Key' },
}

// Helper function to check if a scheme matches a platform's scheme or secondaryScheme
export const matchesPlatformScheme = (
	scheme: string,
	platform: { scheme: string; secondaryScheme?: string }
): boolean => {
	return (
		scheme.toLowerCase() === platform.scheme.toLowerCase() ||
		(platform.secondaryScheme !== undefined && scheme.toLowerCase() === platform.secondaryScheme.toLowerCase())
	)
}

// Helper function to check if user has a social platform connected (checking both scheme and secondaryScheme)
export const isUserSocialConnected = (
	userSocialMedias: any[],
	platform: { scheme: string; secondaryScheme?: string }
) => {
	return userSocialMedias?.some((item: any) => matchesPlatformScheme(item.scheme, platform))
}

// Helper function to get connected social accounts from user's social medias that match given platforms
export const getConnectedSocialAccounts = (userSocialMedias: any[], platforms: any[]) => {
	return platforms
		.filter(platform => isUserSocialConnected(userSocialMedias, platform))
		.map(platform => ({
			scheme: platform.scheme,
		}))
}

// Key used to persist the post-Stripe-connection return URL across the OAuth redirect round-trip,
// which drops the connected_callback_url query param.
export const STRIPE_CONNECTED_CALLBACK_KEY = 'stripe_connected_callback_url'
